import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import DockclawLogo from './redesign/DockclawLogo';
import { footerColumns } from './redesign/data';
import '../pages/HomePage.css';

function isExternal(href) {
  return href.startsWith('http://') || href.startsWith('https://');
}

function NavHref({ href, children, ...props }) {
  if (!isExternal(href)) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
}

function PaperFooter() {
  return (
    <footer className="paper-footer">
      <div className="paper-container">
        <div className="paper-footer-top">
          <div>
            <Link to="/" className="paper-logo" aria-label="DockClaw home">
              <img src="/dlogoblack.svg" alt="DockClaw" className="paper-logo-full" />
            </Link>
            <p>Hire your first AI agentic staff.</p>
          </div>
          <div className="paper-footer-mini">
            {footerColumns.map((column) => (
              <div key={column.title}>
                <b>{column.title}</b>
                {column.links.slice(0, 5).map(([label, href]) => (
                  <NavHref key={label} href={href}>{label}</NavHref>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="paper-footer-bottom">
          <span>© 2026 DockClaw AI, All rights reserved.</span>
          <div className="paper-footer-actions">
            <div className="paper-footer-legal">
              <Link to="/terms">Terms & Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
            <a className="paper-footer-social" href="https://x.com/DockClaw" aria-label="DockClaw on X" target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M13.683 10.622 20.24 3h-1.554l-5.693 6.618L8.446 3H3.201l6.877 10.007L3.201 21h1.554l6.012-6.989L15.569 21h5.245l-7.131-10.378Zm-2.128 2.474-.697-.997L5.315 4.17h2.387l4.473 6.399.697.997 5.815 8.318H16.3l-4.745-6.788Z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function LegacyFooter() {
  return (
    <footer className="relative z-10 px-4 pb-10 pt-8">
      <div
        className="mx-auto max-w-[1200px] rounded-[32px] border border-white/10 p-6 sm:p-8 lg:p-10"
        style={{
          backgroundImage: 'var(--dc-card-gradient)',
        }}
      >
        {/* Footer columns */}
        <div className="flex flex-col gap-10 py-10 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <DockclawLogo className="text-black" />
            <p className="mt-4 max-w-[280px] text-sm leading-6 text-white/45">
              Always-on staff that scout, research, prep, and follow up — reporting back inside the messengers your team already uses.
            </p>
            <address className="mt-5 max-w-[280px] space-y-2.5 text-sm not-italic leading-5 text-white/55">
              <div className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-white/40" aria-hidden="true" />
                <span>131 Continental Dr, Suite 305, Newark, DE 19713, US</span>
              </div>
              <a
                href="tel:+16062214714"
                className="flex items-center gap-2.5 transition-colors hover:text-white"
              >
                <Phone className="h-4 w-4 shrink-0 text-white/40" aria-hidden="true" />
                <span>+1 (606) 221-4714</span>
              </a>
              <a
                href="mailto:support@dockclaw.com"
                className="flex items-center gap-2.5 transition-colors hover:text-white"
              >
                <Mail className="h-4 w-4 shrink-0 text-white/40" aria-hidden="true" />
                <span>support@dockclaw.com</span>
              </a>
            </address>
          </div>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8 xl:gap-10">
            {footerColumns.map((column) => (
              <div key={column.title}>
                <h3 className="text-sm font-semibold text-white text-balance">{column.title}</h3>
                <div className="mt-4 space-y-3">
                  {column.links.map(([label, href]) =>
                    isExternal(href) ? (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-white/50 transition-colors hover:text-white/[0.82]"
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        key={label}
                        to={href}
                        className="block text-sm text-white/50 transition-colors hover:text-white/[0.82]"
                      >
                        {label}
                      </Link>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.08] pt-5 text-center text-sm text-white/40">
          DockClaw &copy; {new Date().getFullYear()} All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export default function Footer() {
  const location = useLocation();

  if (location.pathname.startsWith('/dashboard')) {
    return <LegacyFooter />;
  }

  return <PaperFooter />;
}
